import { HttpClient, HttpHeaders } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { environment } from 'src/environments/environment';
import { Observable, catchError, map, throwError } from 'rxjs';

export interface TemplateCategory {
    id: number;
    name: string;
    description?: string;
    directie_id: number;
    compartiment_id?: number;
    parent_category_id?: number;
    active: boolean;
    public_visible: boolean;
    order_index: number;
    created_at?: string;
    directie_info?: any;
    compartiment_info?: any;
    template_counts?: {
        document_templates: number;
        request_templates: number;
        total_assignments: number;
    };
    children?: TemplateCategory[];
}

export interface TemplateCategoryFormData {
    name: string;
    description?: string;
    directie_id: number;
    compartiment_id?: number;
    parent_category_id?: number;
    active?: boolean;
    public_visible?: boolean;
    order_index?: number;
}

export interface CategoryAssignmentData {
    template_id: number;
    template_type: 'oficial' | 'cerere';
    category_ids: number[];
    set_primary?: boolean;
    primary_category_id?: number;
}

@Injectable({
    providedIn: 'root',
})
export class TemplateCategoriesService {
    private serviceName = 'TemplateCategoriesService';
    private token!: string;
    private basePath = environment.interop.basePath;

    // Template Categories URLs
    private baseUrl = this.basePath + 'api/nomenclatoare/template-categories';
    private organizationUrl = this.basePath + 'api/nomenclatoare/template-organization';

    // Specific endpoint URLs
    private listUrl = this.baseUrl;
    private listDTUrl = this.baseUrl + '/datatable';
    private treeUrl = this.baseUrl + '/tree';
    private typeaheadUrl = this.baseUrl + '/typeahead';
    private byOrganizationUrl = this.baseUrl + '/by-organization';
    private storeUrl = this.baseUrl;
    private updateUrl = this.baseUrl;
    private deleteUrl = this.baseUrl;
    private statisticsUrl = this.baseUrl + '/statistics';
    private migrateUrl = this.baseUrl + '/migrate';

    // Organization URLs
    private treeViewUrl = this.organizationUrl + '/tree-view';
    private templatesByCategoryUrl = this.organizationUrl + '/by-category';
    private assignCategoriesUrl = this.organizationUrl + '/templates';
    private bulkAssignUrl = this.organizationUrl + '/bulk-assign';

    private httpOptions: any;

    constructor(private http: HttpClient) { }

    setToken(token: string) {
        this.token = token;
        this.httpOptions = {
            headers: new HttpHeaders({
                'Content-Type': 'application/json',
                Accept: 'application/json',
                Authorization: `Bearer ${this.token}`,
            }),
        };
    }

    getListDTUrl(): string {
        return this.listDTUrl;
    }

    // Basic CRUD Operations
    getCategories(params?: any): Observable<any> {
        let url = this.listUrl;
        if (params) {
            const searchParams = new URLSearchParams(params);
            url += '?' + searchParams.toString();
        }
        return this.http.get(url, this.httpOptions);
    }

    getTreeStructure(directieId?: number, compartimentId?: number): Observable<any> {
        let url = this.treeUrl;
        const params = new URLSearchParams();

        if (directieId) {
            params.append('directie_id', directieId.toString());
        }
        if (compartimentId) {
            params.append('compartiment_id', compartimentId.toString());
        }

        if (params.toString()) {
            url += '?' + params.toString();
        }

        return this.http.get(url, this.httpOptions);
    }

    getCategoriesByOrganization(directieId: number, compartimentId?: number): Observable<any> {
        let url = `${this.byOrganizationUrl}/${directieId}`;
        if (compartimentId) {
            url += `/${compartimentId}`;
        }
        return this.http.get(url, this.httpOptions);
    }

    getTypeaheadCategories(search: string = '', directieId?: number, compartimentId?: number): Observable<any> {
        const params = new URLSearchParams();
        if (search) params.append('search', search);
        if (directieId) params.append('directie_id', directieId.toString());
        if (compartimentId) params.append('compartiment_id', compartimentId.toString());

        const url = this.typeaheadUrl + (params.toString() ? '?' + params.toString() : '');
        return this.http.get(url, this.httpOptions);
    }

    getCategory(id: number): Observable<any> {
        return this.http.get(`${this.baseUrl}/${id}`, this.httpOptions);
    }

    store(data: TemplateCategoryFormData): Observable<any> {
        return this.http.post(this.storeUrl, data, this.httpOptions);
    }

    update(id: number, data: TemplateCategoryFormData): Observable<any> {
        return this.http.put(`${this.updateUrl}/${id}`, data, this.httpOptions);
    }

    delete(id: number): Observable<any> {
        return this.http.delete(`${this.deleteUrl}/${id}`, this.httpOptions);
    }

    toggleStatus(id: number): Observable<any> {
        return this.http.put(`${this.baseUrl}/${id}/status`, {}, this.httpOptions);
    }

    toggleVisibility(id: number): Observable<any> {
        return this.http.put(`${this.baseUrl}/${id}/visibility`, {}, this.httpOptions);
    }

    // Statistics and Migration
    getStatistics(): Observable<any> {
        return this.http.get(this.statisticsUrl, this.httpOptions);
    }

    migrateTemplates(): Observable<any> {
        return this.http.post(this.migrateUrl, {}, this.httpOptions);
    }

    // Template Organization Operations
    getTemplateTreeView(params?: any): Observable<any> {
        let url = this.treeViewUrl;
        if (params) {
            const searchParams = new URLSearchParams(params);
            url += '?' + searchParams.toString();
        }
        return this.http.get(url, this.httpOptions);
    }

    getTemplatesByCategory(categoryId: number, templateType?: string): Observable<any> {
        let url = `${this.templatesByCategoryUrl}/${categoryId}`;
        if (templateType) {
            url += `?template_type=${templateType}`;
        }
        return this.http.get(url, this.httpOptions);
    }

    assignTemplateToCategories(templateId: number, data: CategoryAssignmentData): Observable<any> {
        return this.http.post(`${this.assignCategoriesUrl}/${templateId}/assign-categories`, data, this.httpOptions);
    }

    removeTemplateFromCategory(templateId: number, categoryId: number, templateType: string): Observable<any> {
        const params = { template_type: templateType };
        const options = {
            ...this.httpOptions,
            body: params
        };
        return this.http.delete(`${this.assignCategoriesUrl}/${templateId}/category/${categoryId}`, options);
    }

    getTemplateCategories(templateId: number, templateType: string): Observable<any> {
        const url = `${this.assignCategoriesUrl}/${templateId}/categories?template_type=${templateType}`;
        return this.http.get(url, this.httpOptions);
    }

    setPrimaryCategory(templateId: number, data: { template_type: string; category_id: number }): Observable<any> {
        return this.http.put(`${this.assignCategoriesUrl}/${templateId}/primary-category`, data, this.httpOptions);
    }

    bulkAssignTemplates(data: {
        template_ids: number[];
        template_type: string;
        category_ids: number[];
        replace_existing?: boolean;
    }): Observable<any> {
        return this.http.post(this.bulkAssignUrl, data, this.httpOptions);
    }

    // Helper methods for data transformation
    transformCategoryTree(categories: TemplateCategory[]): any[] {
        return categories.map(category => ({
            ...category,
            expandable: (category.children && category.children.length > 0),
            level: 0
        }));
    }

    flattenTree(tree: TemplateCategory[], level: number = 0): any[] {
        let result: any[] = [];

        tree.forEach(node => {
            result.push({
                ...node,
                level,
                expandable: (node.children && node.children.length > 0)
            });

            if (node.children && node.children.length > 0) {
                result = result.concat(this.flattenTree(node.children, level + 1));
            }
        });

        return result;
    }

    // Search and filter utilities
    searchInTree(tree: TemplateCategory[], searchTerm: string): TemplateCategory[] {
        if (!searchTerm) return tree;

        const filteredTree: TemplateCategory[] = [];

        tree.forEach(node => {
            const nodeMatches = node.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
                (node.description && node.description.toLowerCase().includes(searchTerm.toLowerCase()));

            let filteredChildren: TemplateCategory[] = [];
            if (node.children) {
                filteredChildren = this.searchInTree(node.children, searchTerm);
            }

            if (nodeMatches || filteredChildren.length > 0) {
                filteredTree.push({
                    ...node,
                    children: filteredChildren
                });
            }
        });

        return filteredTree;
    }

    // Validation helpers
    validateCategoryData(data: TemplateCategoryFormData): string[] {
        const errors: string[] = [];

        if (!data.name || data.name.trim().length === 0) {
            errors.push('Numele categoriei este obligatoriu.');
        }

        if (data.name && data.name.length > 255) {
            errors.push('Numele categoriei nu poate depăși 255 de caractere.');
        }

        if (!data.directie_id || data.directie_id <= 0) {
            errors.push('Direcția este obligatorie.');
        }

        if (data.compartiment_id && data.compartiment_id <= 0) {
            errors.push('Compartimentul selectat nu este valid.');
        }

        if (data.parent_category_id && data.parent_category_id <= 0) {
            errors.push('Categoria părinte selectată nu este validă.');
        }

        if (data.order_index !== undefined && data.order_index < 0) {
            errors.push('Indexul de ordine nu poate fi negativ.');
        }

        return errors;
    }

    // Category path utilities
    getCategoryPath(category: TemplateCategory, allCategories: TemplateCategory[]): string {
        const path: string[] = [];
        let current: TemplateCategory | undefined = category;

        while (current) {
            path.unshift(current.name);

            if (current.parent_category_id) {
                current = allCategories.find(c => c.id === current!.parent_category_id);
            } else {
                current = undefined;
            }
        }

        return path.join(' / ');
    }

    // Template count aggregation
    aggregateTemplateCounts(categories: TemplateCategory[]): { total_documents: number; total_requests: number } {
        let totalDocuments = 0;
        let totalRequests = 0;

        const countRecursive = (cats: TemplateCategory[]) => {
            cats.forEach(cat => {
                if (cat.template_counts) {
                    totalDocuments += cat.template_counts.document_templates || 0;
                    totalRequests += cat.template_counts.request_templates || 0;
                }

                if (cat.children) {
                    countRecursive(cat.children);
                }
            });
        };

        countRecursive(categories);

        return {
            total_documents: totalDocuments,
            total_requests: totalRequests
        };
    }
}